export const TERMS_AND_CONDITIONS = {
  lastUpdated: "January 6, 2026",
  sections: [
    {
      title: "1. Acceptance of Terms",
      content: `By accessing and using heal ("the Service"), you accept and agree to be bound by the terms and provisions of this agreement. If you do not agree to these terms, please do not use the Service.`
    },
    {
      title: "2. Description of Service",
      content: `heal is a health data analysis tool that extracts and organizes health metrics from uploaded medical reports (lab reports, blood tests, etc.). The Service provides:
• Automated extraction of health metrics from documents
• Organization and categorization of health data
• Visual dashboards and trend analysis
• AI-generated health summaries and insights
• Overall health score calculations`
    },
    {
      title: "3. Medical Disclaimer - IMPORTANT",
      content: `⚠️ CRITICAL: heal is NOT a medical device and does NOT provide medical advice.

• This Service is for informational and organizational purposes only
• All extracted data and AI-generated insights are analysis tools, NOT medical diagnoses
• You MUST verify all extracted values with your original medical reports
• You MUST consult qualified healthcare professionals for medical decisions
• DO NOT use this Service as a substitute for professional medical advice, diagnosis, or treatment
• Extraction accuracy may vary; errors may occur in data processing
• We are NOT liable for any decisions made based on information from this Service`
    },
    {
      title: "4. Beta/Demo Service Notice",
      content: `The Service is currently in beta/demo phase. This means:
• Features may change or be discontinued without notice
• Processing times may vary (typically 60-120 minutes for large documents)
• Extraction accuracy is approximately 90% but not guaranteed
• Service availability and uptime are not guaranteed
• Data loss or corruption may occur (always keep original reports)`
    },
    {
      title: "5. User Responsibilities",
      content: `By using the Service, you agree to:
• Provide accurate information during registration
• Keep your account credentials secure and confidential
• Only upload medical reports that belong to you or that you have permission to upload
• Verify all extracted data against original medical reports
• Use the Service in compliance with applicable laws and regulations
• Not use the Service for any illegal, harmful, or fraudulent purposes
• Not attempt to reverse engineer, hack, or compromise the Service
• Not upload files containing viruses, malware, or harmful code`
    },
    {
      title: "6. Data Collection and Usage",
      content: `We collect and process:
• Account information (email, name, profile data)
• Uploaded medical reports and documents
• Extracted health metrics and analysis results
• Usage data and analytics

Your data is:
• Stored securely on encrypted servers
• Used solely to provide the Service to you
• NOT shared with third parties without your consent
• NOT sold to advertisers or data brokers
• Accessible only to you through your account

See our Privacy Policy for complete details.`
    },
    {
      title: "7. Data Retention and Deletion",
      content: `• You may request deletion of your account and all associated data at any time
• Upon deletion request, all your data will be permanently removed within 30 days
• We recommend downloading your data before requesting deletion
• Some anonymized usage data may be retained for analytics purposes`
    },
    {
      title: "8. File Upload Limitations",
      content: `• Maximum file size: 50MB per upload
• Maximum pages: 100 pages per document (LLMWhisperer limitation)
• Supported formats: PDF, JPG, JPEG, PNG, WEBP, HEIC
• Password-protected PDFs require password input
• Processing time varies based on document size and complexity`
    },
    {
      title: "9. Intellectual Property",
      content: `• The Service, including all software, designs, and content, is owned by heal
• Your uploaded documents and extracted data remain your property
• You grant us a limited license to process your documents to provide the Service
• You may not copy, modify, or redistribute any part of the Service without permission`
    },
    {
      title: "10. Limitation of Liability",
      content: `TO THE MAXIMUM EXTENT PERMITTED BY LAW:

• The Service is provided "AS IS" without warranties of any kind
• We are NOT liable for any extraction errors, data loss, or service interruptions
• We are NOT liable for any health decisions made based on the Service
• We are NOT liable for any direct, indirect, or consequential damages
• Our total liability shall not exceed the amount you paid for the Service (currently $0 for free users)
• You use the Service at your own risk`
    },
    {
      title: "11. Privacy and Security",
      content: `We implement reasonable security measures to protect your data:
• Encrypted data transmission (HTTPS/SSL)
• Secure password hashing
• Database encryption for sensitive data
• Regular security updates

However, no system is 100% secure. You acknowledge that:
• Internet transmission is never completely secure
• Unauthorized access or data breaches may occur despite our efforts
• You should keep backup copies of important medical reports`
    },
    {
      title: "12. Termination",
      content: `• You may terminate your account at any time through account settings
• We may suspend or terminate your account for violations of these terms
• We may discontinue the Service at any time with reasonable notice
• Upon termination, your data will be deleted according to our retention policy`
    },
    {
      title: "13. Changes to Terms",
      content: `• We may update these Terms at any time
• Material changes will be notified via email or Service notification
• Continued use after changes constitutes acceptance of new Terms
• If you disagree with changes, you must stop using the Service`
    },
    {
      title: "14. Governing Law",
      content: `These Terms are governed by and construed in accordance with applicable laws. Any disputes shall be resolved through appropriate legal channels in the jurisdiction where the Service is operated.`
    },
    {
      title: "15. Contact Information",
      content: `For questions about these Terms or the Service, contact us through:
• In-app support/feedback
• Email: support@heal.app (or your contact email)
• Profile settings feedback form`
    },
    {
      title: "16. Acknowledgment",
      content: `By checking the agreement box and using the Service, you acknowledge that:
• You have read and understood these Terms
• You agree to be bound by these Terms
• You understand this is NOT a medical service
• You will verify all extracted data with original reports
• You will consult healthcare professionals for medical decisions`
    }
  ]
};

export const PRIVACY_POLICY = {
  lastUpdated: "January 6, 2026",
  sections: [
    {
      title: "1. Information We Collect",
      content: `Personal Information:
• Email address (for account creation and notifications)
• Name and profile information
• Password (stored as encrypted hash)

Health Information:
• Uploaded medical reports (PDFs, images)
• Extracted health metrics (blood test results, vitals, etc.)
• Health categories and trends
• AI-generated health summaries

Technical Information:
• IP address and device information
• Browser type and version
• Usage patterns and analytics
• Error logs and diagnostic data`
    },
    {
      title: "2. How We Use Your Information",
      content: `We use your information to:
• Process and extract health metrics from uploaded documents
• Display health dashboards and visualizations
• Generate AI-powered health insights and summaries
• Send processing completion notifications
• Improve Service accuracy and performance
• Provide customer support
• Ensure Service security and prevent fraud
• Comply with legal obligations`
    },
    {
      title: "3. Data Storage and Security",
      content: `• Data is stored on secure cloud servers (Supabase)
• All data transmission uses SSL/TLS encryption
• Passwords are hashed using industry-standard algorithms
• Database access is restricted and monitored
• Regular security audits and updates are performed
• Uploaded files are processed and stored securely`
    },
    {
      title: "4. Data Sharing and Disclosure",
      content: `We DO NOT sell or share your personal or health data with third parties, except:

Service Providers (with strict confidentiality):
• Cloud hosting providers (for data storage)
• AI/ML processing services (for extraction and analysis)
• Email service providers (for notifications)

Legal Requirements:
• If required by law or legal process
• To protect rights, property, or safety
• In connection with business transfers (merger, acquisition)

YOU control your data. We will NEVER:
• Sell your health data to advertisers
• Share your reports without consent
• Use your data for purposes beyond the Service`
    },
    {
      title: "5. Your Rights and Choices",
      content: `You have the right to:
• Access all your stored data
• Download your health data and reports
• Correct inaccurate information
• Delete your account and all associated data
• Opt-out of non-essential communications
• Request information about data processing

To exercise these rights, use account settings or contact support.`
    },
    {
      title: "6. Data Retention",
      content: `• Active accounts: Data retained as long as account is active
• Deleted accounts: All data permanently deleted within 30 days
• Backup systems: May retain data for up to 90 days for recovery
• Legal holds: Data may be retained if required by law
• Analytics: Anonymized usage data may be retained indefinitely`
    },
    {
      title: "7. Cookies and Tracking",
      content: `We use:
• Authentication cookies (essential for login)
• Local storage (for app preferences and temporary data)
• Analytics (to improve Service performance)

You can disable cookies in browser settings, but this may limit functionality.`
    },
    {
      title: "8. Third-Party Services",
      content: `The Service integrates with:
• Supabase (database and authentication)
• LLMWhisperer (document processing)
• AI/ML providers (health metric extraction)
• Email providers (notifications)

These services have their own privacy policies. We choose providers with strong privacy practices.`
    },
    {
      title: "9. International Data Transfers",
      content: `Your data may be transferred to and processed in countries other than your own. We ensure appropriate safeguards are in place to protect your data in accordance with this Privacy Policy.`
    },
    {
      title: "10. Children's Privacy",
      content: `The Service is NOT intended for users under 18. We do not knowingly collect data from children. If you are under 18, do not use the Service or provide any personal information.`
    },
    {
      title: "11. Changes to Privacy Policy",
      content: `We may update this Privacy Policy to reflect changes in:
• Legal requirements
• Service features
• Data practices

Material changes will be notified via email or in-app notification. Continued use constitutes acceptance.`
    },
    {
      title: "12. Contact Us",
      content: `For privacy questions or to exercise your rights:
• Email: privacy@heal.app
• In-app feedback form
• Account settings`
    }
  ]
};

export const QUICK_SUMMARY = `By using heal, you agree that:
✓ This is NOT medical advice - always consult healthcare professionals
✓ You will verify all extracted data with your original reports
✓ Extraction accuracy may vary (~90% typical, errors possible)
✓ Processing takes 60-120 minutes for large documents
✓ Your data is private and not shared with third parties
✓ You can delete your account and data anytime
✓ This is a beta service and features may change`;
